import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState, useMemo } from 'react';

import { isOption } from 'src/typeGuards';
import type { Option } from 'src/types';

export type OptionState<T> = [
  value: Option<T> | null,
  handleOptionChange: (val: unknown) => void,
  setValue: Dispatch<SetStateAction<Option<T> | null>>,
];

/**
 * A helper to be used with our Select component (e.g. for filters etc, when used standalone (outside of form)).
 * @param defaultValue - An option to be set by default.
 * @returns - OptionState.
 */
export const useOptionState = <T>(defaultValue?: Option<T>): OptionState<T> => {
  const [value, setValue] = useState<Option<T> | null>(defaultValue || null);

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const handleOptionChange = useCallback((val: unknown) => {
    if (isOption<T>(val)) {
      setValue(val);
    } else {
      setValue(null);
    }
  }, []);

  return [value, handleOptionChange, setValue] as OptionState<T>;
};

export type OptionStateMulti<T> = {
  value: Option<T>[] | null;
  handleOptionChange: (val: unknown) => void;
  setValue: Dispatch<SetStateAction<Option<T>[] | null>>;
  valuesArray?: T[];
};

/**
 * A helper to be used with our Select component (e.g. for filters etc, when used standalone (outside of form)).
 * Similar to useOptionState, but for multiple/creatable selects and has a memoized array of values.
 * @param defaultValue - An array of options to be set by default.
 * @returns OptionStateMulti.
 */
export const useMultiOptionState = <T>(defaultValue?: Option<T>[]): OptionStateMulti<T> => {
  const [value, setValue] = useState<Option<T>[] | null>(defaultValue || null);

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const handleOptionChange = useCallback((val: unknown) => {
    if (Array.isArray(val) && val.length && val.every((v) => isOption<T>(v))) {
      setValue(val);
    } else {
      setValue(null);
    }
  }, []);

  const valuesArray: T[] | undefined = useMemo(() => value?.map((option) => option.value), [value]);

  return useMemo(
    () => ({
      value,
      handleOptionChange,
      setValue,
      valuesArray,
    }),
    [value, handleOptionChange, setValue, valuesArray],
  );
};
